import MightLike, { RECOMMENDATION_TYPES } from "../mightLike/MightLike";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import Grid from "../grid/Grid";
import useRecommendations from "../mightLike/useRecommendations";
import { YMAL_BTL_HEADER, PAGE_AREAS } from "../../constants";
import MightLikeFailed from "../mightLike/MightLikeFailed";
import MightLikeNoItems from "../mightLike/MightLikeNoItems";
import ProductTile from "../productTile/ProductTile";
import { buildProductTileAriaLabel } from "../productTile/buildProductTileAriaLabel";
import useDiscountPercentage from "../../hooks/useDiscountPercentage";
import TitleThenPrice from "../productTile/TitleThenPrice";
import { Price } from "../../types/productTypes/price";
import styles from "./WaysToStyleSimilarItems.scss";

interface MightLikeProduct {
  productId: number;
  url: string;
  imageUrl: string;
  productTitle: string;
  brandName: string;
  colourWayId: number;
  priceData: Price;
}

export interface WaysToStyleSimilarItemsProps {
  selectedProductId?: number;
}

function getProductTiles({
  mightLikeProducts,
  showDiscountPercentage,
  getTranslation,
}: {
  mightLikeProducts: MightLikeProduct[];
  showDiscountPercentage: boolean;
  getTranslation: (key: string) => string;
}) {
  return mightLikeProducts.map(
    ({ url, productTitle, priceData, colourWayId, productId, imageUrl }) => (
      <ProductTile
        key={url}
        url={url}
        title={productTitle}
        colourWayId={colourWayId}
        productId={productId}
        imageUrl={imageUrl}
        priceData={priceData}
        pageArea={PAGE_AREAS.WAYS_TO_STYLE_SIMILAR_ITEMS}
        shouldShowTitle={true}
        productTileAriaLabel={buildProductTileAriaLabel({
          getTranslation,
          title: productTitle,
          priceData,
          showDiscountPercentage,
        })}
      >
        <TitleThenPrice priceData={priceData} title={productTitle} />
      </ProductTile>
    )
  );
}

export default function WaysToStyleSimilarItems({
  selectedProductId,
}: WaysToStyleSimilarItemsProps) {
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };

  const { mightLikeProducts, hasFetchedRecs, apiFailed } = useRecommendations({
    productId: selectedProductId,
    scope: YMAL_BTL_HEADER,
    pageArea: PAGE_AREAS.WAYS_TO_STYLE_SIMILAR_ITEMS,
  }) as {
    mightLikeProducts: MightLikeProduct[] | null;
    hasFetchedRecs: boolean;
    apiFailed: boolean;
  };

  const { showDiscountPercentage } = useDiscountPercentage();

  if (!selectedProductId) {
    return null;
  }

  if (apiFailed) {
    return (
      <MightLikeFailed additionalClasses={styles.waysToStyleSimilarItems} />
    );
  }

  if (!mightLikeProducts || (hasFetchedRecs && !mightLikeProducts.length)) {
    return (
      <MightLikeNoItems additionalClasses={styles.waysToStyleSimilarItems} />
    );
  }

  return (
    <MightLike
      title={getTranslation("pdp_carousel_recommendations_title_similar_items")}
      titleAriaLabel={getTranslation(
        "pdp_carousel_recommendations_out_of_stock_aria"
      )}
      id="waysToStyleMightLike"
      itemCount={mightLikeProducts?.length || 0}
      type={RECOMMENDATION_TYPES.GRID_WAYS_TO_STYLE_MOBILE}
    >
      {[
        <Grid
          key="grid"
          type={RECOMMENDATION_TYPES.GRID_WAYS_TO_STYLE_MOBILE}
          layoutType="twoColumn"
          gridTestId="waysToStyleSimilarItems"
        >
          {getProductTiles({
            mightLikeProducts,
            showDiscountPercentage,
            getTranslation,
          })}
        </Grid>,
      ]}
    </MightLike>
  );
}
